import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSkeleton,
  Modal,
  Select,
} from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../../shared/utils/format';
import type { BillingScheduleItem, BillingStatus } from './types';
import {
  useBillingSchedule,
  useCreateBillingSchedule,
  useGenerateBillingSchedule,
  useUpdateBillingScheduleItem,
  useContract,
} from './useContractQueries';

/* ── Cronograma de Faturamento de Contrato (R2C, tela 7)
 * Consome GET/POST /contracts/:id/billing-schedule, POST .../generate e
 * PATCH /contracts/:id/billing-schedule/:itemId.
 * Protótipo: Inst., Competence, Due Date, Amount, Status, Action.
 */

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  BILLED: 'Faturado',
  PAID: 'Pago',
  CANCELLED: 'Cancelado',
};

const STATUS_VARIANT: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  BILLED: 'info',
  PAID: 'success',
  CANCELLED: 'error',
};

const STATUS_OPTIONS: BillingStatus[] = ['PENDING', 'BILLED', 'PAID', 'CANCELLED'];

function isOverdue(item: BillingScheduleItem): boolean {
  if (item.status !== 'PENDING') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(`${item.dueDate.slice(0, 10)}T00:00:00`) < today;
}

interface ScheduleSummary {
  totalAmount: number;
  PENDING: number;
  BILLED: number;
  PAID: number;
  CANCELLED: number;
  OVERDUE: number;
}

export function ContractBillingSchedulePage(): JSX.Element {
  const params = useParams({ strict: false }) as { contractId?: string };
  const contractId = params.contractId ?? '';

  const { data: contract } = useContract(contractId);
  const { data, isLoading, error } = useBillingSchedule(contractId);
  const createItems = useCreateBillingSchedule(contractId);
  const generate = useGenerateBillingSchedule(contractId);
  const updateItem = useUpdateBillingScheduleItem(contractId);

  const [showGenerate, setShowGenerate] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showEdit, setShowEdit] = useState<BillingScheduleItem | null>(null);
  const [actionError, setActionError] = useState('');

  /* Gerar */
  const [installments, setInstallments] = useState('12');
  const [dueDays, setDueDays] = useState('5');

  /* Manual */
  const [mInstallment, setMInstallment] = useState('1');
  const [mCompetence, setMCompetence] = useState('');
  const [mDueDate, setMDueDate] = useState('');
  const [mAmount, setMAmount] = useState('');

  /* Editar item */
  const [eCompetence, setECompetence] = useState('');
  const [eDueDate, setEDueDate] = useState('');
  const [eAmount, setEAmount] = useState('');

  const rows = useMemo(() => data?.data ?? [], [data]);

  const summary = useMemo<ScheduleSummary>(() => {
    const totals: ScheduleSummary = {
      totalAmount: 0,
      PENDING: 0,
      BILLED: 0,
      PAID: 0,
      CANCELLED: 0,
      OVERDUE: 0,
    };
    for (const r of rows) {
      totals[r.status as keyof ScheduleSummary] =
        (totals[r.status as keyof ScheduleSummary] ?? 0) + r.amount;
      totals.totalAmount += r.amount;
      if (isOverdue(r)) totals.OVERDUE += r.amount;
    }
    return totals;
  }, [rows]);

  const openGenerate = (): void => {
    setInstallments('12');
    setDueDays('5');
    setActionError('');
    setShowGenerate(true);
  };

  const submitGenerate = (): void => {
    const n = Number(installments);
    if (!Number.isInteger(n) || n < 1) {
      setActionError('Número de parcelas deve ser inteiro ≥ 1.');
      return;
    }
    setActionError('');
    generate.mutate(
      { installments: n, ...(dueDays ? { dueDays: Number(dueDays) } : {}) },
      {
        onSuccess: () => setShowGenerate(false),
        onError: (err) => setActionError((err as Error).message),
      },
    );
  };

  const openManual = (): void => {
    setMInstallment(String(rows.length + 1));
    setMCompetence('');
    setMDueDate('');
    setMAmount('');
    setActionError('');
    setShowManual(true);
  };

  const submitManual = (): void => {
    const installment = Number(mInstallment);
    const amount = Number(mAmount);
    if (!Number.isInteger(installment) || installment < 1) {
      setActionError('Parcela deve ser inteira ≥ 1.');
      return;
    }
    if (!mCompetence || !mDueDate) {
      setActionError('Informe competência e vencimento.');
      return;
    }
    if (!Number.isFinite(amount) || amount < 0) {
      setActionError('Valor deve ser um número ≥ 0.');
      return;
    }
    setActionError('');
    createItems.mutate(
      [
        {
          installment,
          competence: mCompetence,
          dueDate: mDueDate,
          amount,
        },
      ],
      {
        onSuccess: () => setShowManual(false),
        onError: (err) => setActionError((err as Error).message),
      },
    );
  };

  const openEdit = (item: BillingScheduleItem): void => {
    setECompetence(item.competence.slice(0, 10));
    setEDueDate(item.dueDate.slice(0, 10));
    setEAmount(String(item.amount));
    setActionError('');
    setShowEdit(item);
  };

  const submitEdit = (): void => {
    if (!showEdit) return;
    const amount = Number(eAmount);
    if (!mCompetence && !eCompetence && !eDueDate) {
      setActionError('Informe ao menos um campo para alterar.');
      return;
    }
    if (eAmount !== '' && (!Number.isFinite(amount) || amount < 0)) {
      setActionError('Valor deve ser um número ≥ 0.');
      return;
    }
    setActionError('');
    updateItem.mutate(
      {
        itemId: showEdit.id,
        input: {
          ...(eCompetence ? { competence: eCompetence } : {}),
          ...(eDueDate ? { dueDate: eDueDate } : {}),
          ...(eAmount !== '' ? { amount } : {}),
        },
      },
      {
        onSuccess: () => setShowEdit(null),
        onError: (err) => setActionError((err as Error).message),
      },
    );
  };

  const changeStatus = (item: BillingScheduleItem, status: string): void => {
    updateItem.mutate({ itemId: item.id, input: { status: status as BillingStatus } });
  };

  if (!contractId) {
    return (
      <div className="p-xl">
        <Card>
          <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">
            Nenhum contrato selecionado
          </p>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Selecione um contrato para ver a agenda de faturamento.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-xl flex flex-col gap-lg">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-md">
        <div>
          <p className="mb-xs font-label-sm text-label-sm uppercase tracking-wide text-on-surface-variant">
            {contract ? `Contrato ${contract.id}` : 'Cronograma de faturamento'}
          </p>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
            Agenda de Faturamento
          </h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {contract
              ? `${contract.items[0]?.description ?? 'Contrato'} · ${formatCurrency(contract.value)}/${
                  contract.periodicity
                }`
              : 'Parcelas programadas por competência e vencimento.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="outline" onClick={openManual}>
            + Parcela manual
          </Button>
          <Button variant="primary" onClick={openGenerate}>
            Gerar cronograma
          </Button>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 gap-md lg:grid-cols-5">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Total agendado
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(summary.totalAmount)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Pendente
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(summary.PENDING ?? 0)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Vencido
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-error">
            {formatCurrency(summary.OVERDUE ?? 0)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Faturado
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {formatCurrency(summary.BILLED ?? 0)}
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
            Pago
          </p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-success">
            {formatCurrency(summary.PAID ?? 0)}
          </p>
        </Card>
      </div>

      {actionError ? (
        <p className="font-body-sm text-body-sm text-error">{actionError}</p>
      ) : null}

      {/* Tabela */}
      {isLoading ? (
        <LoadingSkeleton count={5} height="48px" />
      ) : error ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">
            {(error as Error).message}
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">
            Nenhuma parcela agendada
          </p>
          <p className="mb-md m-0 font-body-md text-body-md text-on-surface-variant">
            Gere o cronograma automaticamente a partir do valor do contrato ou
            adicione parcelas manuais.
          </p>
          <Button variant="primary" onClick={openGenerate}>
            Gerar cronograma
          </Button>
        </Card>
      ) : (
        <Card padding="0">
          <div className="overflow-x-auto">
            <table className="w-full text-left font-body-sm">
              <thead className="bg-surface font-label-sm text-on-surface-variant">
                <tr>
                  <th className="p-md font-medium">Inst.</th>
                  <th className="p-md font-medium">Competência</th>
                  <th className="p-md font-medium">Vencimento</th>
                  <th className="p-md font-medium text-right">Valor (R$)</th>
                  <th className="p-md font-medium">Status</th>
                  <th className="p-md font-medium text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => {
                  const overdue = isOverdue(item);
                  const statusLabel = overdue
                    ? 'Vencido'
                    : STATUS_LABEL[item.status] ?? item.status;
                  const statusVariant = overdue
                    ? 'error'
                    : STATUS_VARIANT[item.status] ?? 'default';
                  return (
                    <tr
                      key={item.id}
                      className={`border-t border-outline-variant/60 hover:bg-surface-container-highest ${
                        overdue ? 'bg-error-container/20' : ''
                      }`}
                    >
                      <td className="p-md font-label-md">{item.installment}</td>
                      <td className="p-md">{formatDate(item.competence)}</td>
                      <td className="p-md">{formatDate(item.dueDate)}</td>
                      <td className="p-md text-right font-label-md tabular-nums">
                        {formatCurrency(item.amount)}
                      </td>
                      <td className="p-md">
                        <Badge variant={statusVariant}>
                          {statusLabel}
                        </Badge>
                      </td>
                      <td className="p-md">
                        <div className="flex items-center justify-end gap-sm">
                          {item.status === 'PENDING' || item.status === 'BILLED' ? (
                            <div className="w-32">
                              <Select
                                aria-label={`Status da parcela ${item.installment}`}
                                selectSize="sm"
                                value={item.status}
                                onChange={(e) => changeStatus(item, e.target.value)}
                              >
                                {STATUS_OPTIONS.map((s) => (
                                  <option key={s} value={s}>
                                    {STATUS_LABEL[s]}
                                  </option>
                                ))}
                              </Select>
                            </div>
                          ) : (
                            <span className="font-label-sm text-label-sm text-on-surface-variant">
                              —
                            </span>
                          )}
                          <button
                            type="button"
                            className="font-label-md text-label-md text-primary hover:underline"
                            onClick={() => openEdit(item)}
                          >
                            Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal gerar cronograma */}
      <Modal
        isOpen={showGenerate}
        onClose={() => setShowGenerate(false)}
        title="Gerar cronograma de faturamento"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowGenerate(false)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={generate.isPending} onClick={submitGenerate}>
              Gerar
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-md">
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Gera parcelas a partir do valor e da periodicidade do contrato
            ({contract ? formatCurrency(contract.value) : '—'}).
          </p>
          <Input
            id="gen-installments"
            label="Número de parcelas *"
            type="number"
            min="1"
            step="1"
            value={installments}
            onChange={(e) => setInstallments(e.target.value)}
          />
          <Input
            id="gen-duedays"
            label="Vencimento em dias após a competência"
            type="number"
            min="0"
            step="1"
            value={dueDays}
            onChange={(e) => setDueDays(e.target.value)}
          />
          {actionError ? (
            <p className="mb-0 font-body-sm text-body-sm text-error">{actionError}</p>
          ) : null}
        </div>
      </Modal>

      {/* Modal parcela manual */}
      <Modal
        isOpen={showManual}
        onClose={() => setShowManual(false)}
        title="Adicionar parcela manual"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowManual(false)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={createItems.isPending} onClick={submitManual}>
              Adicionar
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <Input
            id="manual-installment"
            label="Parcela (Inst.) *"
            type="number"
            min="1"
            step="1"
            value={mInstallment}
            onChange={(e) => setMInstallment(e.target.value)}
          />
          <Input
            id="manual-amount"
            label="Valor (R$) *"
            type="number"
            min="0"
            step="0.01"
            value={mAmount}
            onChange={(e) => setMAmount(e.target.value)}
          />
          <Input
            id="manual-competence"
            label="Competência *"
            type="date"
            value={mCompetence}
            onChange={(e) => setMCompetence(e.target.value)}
          />
          <Input
            id="manual-duedate"
            label="Vencimento *"
            type="date"
            value={mDueDate}
            onChange={(e) => setMDueDate(e.target.value)}
          />
        </div>
        {actionError ? (
          <p className="mb-0 mt-sm font-body-sm text-body-sm text-error">{actionError}</p>
        ) : null}
      </Modal>

      {/* Modal editar parcela */}
      <Modal
        isOpen={showEdit !== null}
        onClose={() => setShowEdit(null)}
        title={`Editar parcela ${showEdit?.installment ?? ''}`}
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowEdit(null)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={updateItem.isPending} onClick={submitEdit}>
              Salvar
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-md sm:grid-cols-3">
          <Input
            id="edit-competence"
            label="Competência"
            type="date"
            value={eCompetence}
            onChange={(e) => setECompetence(e.target.value)}
          />
          <Input
            id="edit-duedate"
            label="Vencimento"
            type="date"
            value={eDueDate}
            onChange={(e) => setEDueDate(e.target.value)}
          />
          <Input
            id="edit-amount"
            label="Valor (R$)"
            type="number"
            min="0"
            step="0.01"
            value={eAmount}
            onChange={(e) => setEAmount(e.target.value)}
          />
        </div>
        {actionError ? (
          <p className="mb-0 mt-sm font-body-sm text-body-sm text-error">{actionError}</p>
        ) : null}
      </Modal>
    </div>
  );
}